<template>
  <img v-bind="$attrs" ref="imageRef" :src="this.source" @load="handleLoad" @error="handleError" />
</template>

<script>
import Logger from './logger';
import Url from './url';

const inBrowser = typeof window !== 'undefined' && window !== null;

const hasIntersectionObserver = checkIntersectionObserver();

function checkIntersectionObserver() {
  if (inBrowser
    && 'IntersectionObserver' in window
    && 'IntersectionObserverEntry' in window
    && 'intersectionRatio' in window.IntersectionObserverEntry.prototype) {
    // Minimal polyfill for Edge 15's lack of `isIntersecting`
    // See: https://github.com/w3c/IntersectionObserver/issues/211

    if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
      Object.defineProperty(
        window.IntersectionObserverEntry.prototype,
        'isIntersecting', {
          get() {
            return this.intersectionRatio > 0;
          },
        },
      );
    }
    return true;
  }
  return false;
}

// 默认图片占位
const DefaultSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
// 探测浏览器是否支持webp
const isSupportWebp = (() => {
  if (typeof document !== undefined) {
    return document.createElement('canvas').toDataURL('image/webp')
      .indexOf('data:image/webp') === 0;
  }
  if (typeof wx !== undefined) {
    return true;
  }
  return false;
})();


export default {
  props: {
    src: {
      type: String,
      default: '',
    },
	  defaultSrc: {
      type: String,
      default: DefaultSrc,
    },
    // 支持关闭webp功能
    webp: {
      type: Boolean,
      default: true,
    },
    width: {
      type: String,
      default: '',
    },
    height: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      intersectionObserver: null,
      source: this.defaultSrc,
      startTime: 0,
    };
  },
  mounted() {
    this.$nextTick(function () {
      // 放在下一次渲染中监听
      this.registerObserve();
    });
  },
  watch: {
    src(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.registerObserve();
      }
    },
  },
  methods: {
    registerObserve() {
      this.intersectionObserver && this.intersectionObserver.disconnect();
      if (hasIntersectionObserver && this.$refs.imageRef) {
        this.intersectionObserver = new IntersectionObserver(((entries, observer) => {
          if (entries[0].isIntersecting) {
            this.startLoadImage();
          }
        }), {
          root: null,
          rootMargin: '0%',
          threshold: 0,
        });
        this.intersectionObserver.observe(this.$refs.imageRef);
      } else {
        this.startLoadImage();
      }
    },
    startLoadImage()  {
      const handleSrc = this.getRealSource(this.src);
      this.startTime = new Date().getTime();

      const image = new Image();
      image.onload = () => {
        this.source = handleSrc;
        this.intersectionObserver && this.intersectionObserver.disconnect();
      };
      image.onerror = (err) => {
        this.source = this.src;
        this.intersectionObserver && this.intersectionObserver.disconnect();
      };
      image.src = handleSrc;
    },
    getSize() {
      const renderWidth = this.$refs.imageRef && this.$refs.imageRef.width;
      const renderHeight = this.$refs.imageRef && this.$refs.imageRef.height;
      if (renderWidth && renderHeight) {
        return {
          // 2倍图
          width: renderWidth * 2,
          height: renderHeight * 2,
        };
      }
      // eslint-disable-next-line radix
      const widthIntFromProps = parseInt(String(this.width), 10);
      // eslint-disable-next-line radix
      const heightIntFromProps = parseInt(String(this.height), 10);
      return {
        width: widthIntFromProps,
        height: heightIntFromProps,
      };
    },
    getRealSource(src) {
      // 非cdn图片，或者svg不用加万象
      if (src.indexOf('.myqcloud.com/') < 0 || src.indexOf('.svg') > -1) return src;

      const { webp } = this;
      const supportWebpMimes = ['.jpg', '.jpeg', '.png', '.gif'];
      const useWebp = supportWebpMimes.some(mime => src.indexOf(mime) > -1)
        && isSupportWebp
        && webp;

      const [path = '', query = ''] = src.split('?');
      const thumbnailRegE = /\/thumbnail\/[^/]+/;
      const webpFormat = '/format/webp';

      const thumbnailRet = thumbnailRegE.exec(query);
      let thumbnailStr = (thumbnailRet && thumbnailRet[0]) || '';
      // 兼容外面的万象
      if (!thumbnailRegE.test(query)) {
        const size = this.getSize();
        if (size) {
          const { width, height } = size;
          thumbnailStr = `/thumbnail/${width}x${height}r`;
        }
      }

      let imageMogr = `?imageMogr2${thumbnailStr}`;
      if (useWebp) {
        imageMogr += webpFormat;
      }

      return `${path}${imageMogr}`;
    },
    handleLoad() {
      if (!this.startTime) return;
      const size = this.getSize() || {};
      const loadTime = new Date().getTime();
      Logger.uplog('INFO', `${size.width}*${size.height}`, {
        logType: 'res',
        model: 'image',
        page: Url.getPage(),
        total: `${loadTime - this.startTime}`,
        args: {
          originSrc: this.src,
          sdkName: 'nb-h5-image',
          PicUrl: this.source,
          Network: '',
          LoadType: 'success',
          Error: '',
        },
      });
    },
    handleError(err) {
      if (!this.startTime) return;

      const size = this.getSize() || {};
      const loadTime = new Date().getTime();
      Logger.uplog('INFO', `${size.width}*${size.height}`, {
        logType: 'res',
        model: 'image',
        page: Url.getPage(),
        errCode: '128502',
        total: `${loadTime - this.startTime}`,
        args: {
          originSrc: this.src,
          sdkName: 'nb-h5-image',
          PicUrl: this.source,
          Network: '',
          LoadType: 'fail',
          Error: JSON.stringify(err),
        },
      });
    },
  },
  // render() {
  //   return <img {...this.$attrs} ref={'imageRef'} src={this.source} on={{ error: this.handleError }} />;
  // },
};
</script>
